import axios, {AxiosInstance, AxiosRequestConfig} from 'axios'
import {
    GitLabBranch,
    GitLabGroup,
    GitLabPipeline,
    GitLabPipelineDetails,
    GitLabPipelineJobs,
    GitLabPipelineSchedule,
    GitLabPipelineScheduleDetails,
    GitLabProject
} from '@/interfaces/gitlab'

class GitLabGraphQLClient {
    private client: AxiosInstance;

    constructor(url: string, token: string, clientConfig?: AxiosRequestConfig) {
        this.client = axios.create({
            baseURL: `${url}/api/graphql`,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            timeout: 10 * 1000,
            ...clientConfig,
        });
    }

    public async query<T>(query: string, variables?: Record<string, any>): Promise<T> {
        try {
            const response = await this.client.post('', {
                query,
                variables,
            });
            if (response.data.errors) {
                throw new Error(`GraphQL errors: ${JSON.stringify(response.data.errors)}`);
            }
            return response.data.data as T;
        } catch (error) {
            throw new Error(`GraphQL query failed: ${error}`);
        }
    }
}

class GitlabRestClient {
    private client: AxiosInstance
    private apiBase = '/api/v4'

    constructor(url: string, token: string, clientConfig?: AxiosRequestConfig) {
        this.client = axios.create({
            baseURL: url,
            headers: {
                'Private-Token': token,
            },
            timeout: 10 * 1000,
            ...clientConfig,
        })
    }

    async get(apiPath: string, params?: object) {
        const url = this.apiBase + apiPath
        const {data} = await this.client.get(url, {params})
        return data
    }
}

class GitLab {
    private clientRest: GitlabRestClient
    private clientGraphQL: GitLabGraphQLClient

    constructor(url: string, token: string, clientConfig?: AxiosRequestConfig) {
        this.clientRest = new GitlabRestClient(url, token, clientConfig)
        this.clientGraphQL = new GitLabGraphQLClient(url, token, clientConfig)
    }

    public searchGroups(search: string, options?: any): Promise<GitLabGroup[]> {
        return this.clientRest.get('/groups', {search, ...options})
    }

    public searchProjects(full_path_with_namespace: string, options?: any): Promise<GitLabProject[]> {

        const archived = false
        const order_by = "similarity"
        const per_page = 100
        const simple = true

        const name = full_path_with_namespace.split("/").pop()
        return this.clientRest.get(`/projects`, {search: name, archived, order_by, simple, per_page, ...options})
            .then((response: GitLabProject[]) => {
                return response.filter(r => r.path_with_namespace === full_path_with_namespace)
            })
    }

    public async getAllProjects(groupId: number, include_shared_projects:boolean, options?: any): Promise<GitLabProject[]> {
        let projects = [] as GitLabProject[]
        let page = 1
        const perPage = 100
        while (page === 1 || projects.length === perPage) {
            const result = await this.clientRest.get(`/groups/${groupId}/projects`, {
                archived: false,
                ...options,
                per_page: perPage,
                page,
            })
            projects = projects.concat(result)
            page++
        }
        if (include_shared_projects) {
            return projects
        }
        return projects.filter(project => project.namespace.id === groupId)
    }

    public getLatestPipeline(projectId: number, ref?: string): Promise<GitLabPipelineDetails> {
        const params = ref ? {ref: ref} : {};
        return this.clientRest.get(`/projects/${projectId}/pipelines/latest`, params)
    }

    public getPipelines(projectId: number, ref?: string, options?: object): Promise<GitLabPipeline[]> {
        const params = ref ? {ref: ref} : {};
        return this.clientRest.get(`/projects/${projectId}/pipelines`, {params, ...options})
    }

    public getPipeline(projectId: number, pipelineId: number): Promise<GitLabPipelineDetails> {
        return this.clientRest.get(`/projects/${projectId}/pipelines/${pipelineId}`)
    }

    public getPipelineSchedules(projectId: number): Promise<GitLabPipelineScheduleDetails[]> {
        const params = {
            scope: "active"
        };
        return this.clientRest.get(`/projects/${projectId}/pipeline_schedules`, params)
            .then(
                schedules => Promise.all(schedules.map((schedule: GitLabPipelineSchedule) => {
                    return this.clientRest.get(`/projects/${projectId}/pipeline_schedules/${schedule.id}`, params) as Promise<GitLabPipelineScheduleDetails>
                }))
            )
    }

    // see https://docs.gitlab.com/ee/api/jobs.html#list-pipeline-jobs
    public getPipelineJobs(projectId: number, pipelineId: number, scopes: string[]): Promise<GitLabPipelineJobs[]> {
        const params = {
            include_retries: true,
            scope: scopes
        };
        return this.clientRest.get(`/projects/${projectId}/pipelines/${pipelineId}/jobs`, params)
    }

    public getBranches(project_id: number): Promise<GitLabBranch[]> {
        return this.clientRest.get(`/projects/${project_id}/repository/branches`)
    }

    private async get(apiPath: string, params?:object) {
        const {data} = await this.clientRest.get(apiPath, {params})
        return data
    }
}

export default GitLab
